/**
 * Private notes about a contact (2026-10-06): the things worth remembering
 * about someone — a sister's name, a move in spring. Only you can read
 * them; the API has no route that hands them to the other person.
 *
 * They come back through the relationships API, which carries the unlock
 * header, so a pair behind your secret code has no notes to show while the
 * vault is shut. Every view renders nothing on an error rather than the last
 * note it held.
 */

import { useEffect, useState } from 'react';
import { AlertCircle, ChevronDown, StickyNote } from 'lucide-react';
import { CONTACT_NOTE_MAX_CHARS } from '@toiyp/shared';
import { apiErrorMessage, formatStamp } from '../letters/ui';
import { useNoteQuery, useSaveNoteMutation } from '../relationships/api';

/** The editable notes card: the contact's summary page, and the letter view. */
export function NotesCard({ relationshipId, peerName }: { relationshipId: string; peerName: string }) {
  const { data, isError, isLoading } = useNoteQuery({ relationshipId });
  const [saveNote, { isLoading: saving }] = useSaveNoteMutation();
  const [body, setBody] = useState('');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data && !editing) setBody(data.body);
  }, [data, editing]);

  if (isError || isLoading || !data) return null;

  const onSave = async () => {
    setError(null);
    try {
      await saveNote({ relationshipId, body }).unwrap();
      setEditing(false);
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not save your notes'));
    }
  };

  return (
    <section className="notes-card" aria-label={`Your notes about @${peerName}`}>
      <header className="notes-card__head">
        <StickyNote size={14} aria-hidden="true" />
        <span className="notes-card__title">Your notes</span>
        <span className="notes-card__private">only you can see these</span>
      </header>

      {editing ? (
        <>
          <label htmlFor={`notes-${relationshipId}`} className="visually-hidden">
            Your notes about @{peerName}
          </label>
          <textarea
            id={`notes-${relationshipId}`}
            className="form-textarea notes-card__input"
            rows={4}
            maxLength={CONTACT_NOTE_MAX_CHARS}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={`Things to remember about @${peerName} — names, plans, what they were waiting on…`}
            autoFocus
          />
          {error && (
            <p className="form-error" role="alert">
              <AlertCircle size={13} aria-hidden="true" /> {error}
            </p>
          )}
          <div className="notes-card__actions">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setBody(data.body); setEditing(false); }}>
              Cancel
            </button>
            <button type="button" className="btn btn--primary btn--sm" disabled={saving} onClick={() => void onSave()}>
              {saving ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        </>
      ) : data.body ? (
        <>
          <p className="notes-card__body">{data.body}</p>
          <div className="notes-card__actions">
            {data.updatedAt && (
              <span className="notes-card__when">edited {formatStamp(data.updatedAt)}</span>
            )}
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(true)}>
              Edit
            </button>
          </div>
        </>
      ) : (
        <button type="button" className="notes-card__empty" onClick={() => setEditing(true)}>
          Add a note to remember something about @{peerName}
        </button>
      )}
    </section>
  );
}

/**
 * Above the composer: your notes, folded away until you want them. Nothing at
 * all when there is no note — an empty fold is just clutter.
 */
export function ComposerNotes({ relationshipId, peerName }: { relationshipId: string; peerName: string }) {
  const { data, isError } = useNoteQuery({ relationshipId });
  if (isError || !data?.body) return null;
  return (
    <details className="composer-notes">
      <summary className="composer-notes__summary">
        <StickyNote size={13} aria-hidden="true" />
        Your notes about @{peerName}
        <ChevronDown size={13} className="composer-notes__chev" aria-hidden="true" />
      </summary>
      <p className="composer-notes__body">{data.body}</p>
    </details>
  );
}
